<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'SelectActiveBranchModal',
  computed: {
    ...mapGetters('stripe', ['getAppointmentBranchesLimit']),
    ...mapGetters(['getCurrentAccountId']),
    filteredBranches() {
      if (!this.searchQuery) {
        return this.branchList;
      }
      const query = this.searchQuery.toLowerCase();
      return this.branchList.filter(branch => 
        branch.branch_name.toLowerCase().includes(query)
      );
    },
    isSubmitDisabled() {
      if (this.getAppointmentBranchesLimit === '-1') {
        return this.selectedBranchIds.length === 0;
      }
      return this.selectedBranchIds.length === 0 || 
            this.selectedBranchIds.length > parseInt(this.getAppointmentBranchesLimit, 10);
    },
    isAllSelected() {
      if (this.filteredBranches.length === 0) return false;
      return this.filteredBranches.every(branch => 
        this.selectedBranchIds.includes(branch.branch_id)
      );
    },
    isIndeterminate() {
      const selectedCount = this.filteredBranches.filter(branch =>
        this.selectedBranchIds.includes(branch.branch_id)
      ).length;
      return selectedCount > 0 && selectedCount < this.filteredBranches.length;
    },
    selectionStatusClass() {
      const limit = Number(this.getAppointmentBranchesLimit)
      const count = this.selectedBranchIds.length

      if (limit === -1) return 'text-slate-500'

      if (count > limit) return 'text-red-600 font-semibold'

      if (count === limit) return 'text-slate-700 dark:text-slate-300 font-medium'

      return 'text-slate-500'
    }
  },
  data() {
    return {
      selectedBranchIds: [],
      branchList: [],
      searchQuery: '',
    };
  },
  methods: {
    async updateActiveBranches() {
      try {
        const response = await this.$store.dispatch(
          'branch/updateActiveBranches',
          this.selectedBranchIds
        );

        if (response?.detail?.length) {
          const messages = response.detail.map(err => err.msg).join(', ');
          const errorMessage = 'Failed to update active branches: ' + messages;
          useAlert(errorMessage);
          throw new Error(errorMessage);
        }

        useAlert('Successfully updated active branches.');
        this.$emit('refresh');
        this.$emit('close');
      } catch (err) {
        console.error('Update active branches failed:', err);
        useAlert('An error occurred while updating active branches.');
      }
    },
    isBranchDisabled(branchId) {
      if (this.getAppointmentBranchesLimit === '-1') return false;
      return this.selectedBranchIds.length >= parseInt(this.getAppointmentBranchesLimit, 10) &&
            !this.selectedBranchIds.includes(branchId);
    },
    toggleSelectAll() {
      if (this.isAllSelected) {
        // Deselect all filtered branches
        const filteredIds = this.filteredBranches.map(b => b.branch_id);
        this.selectedBranchIds = this.selectedBranchIds.filter(
          id => !filteredIds.includes(id)
        );
      } else {
        // Select all filtered branches (respecting limit)
        const filteredIds = this.filteredBranches.map(b => b.branch_id);
        const limit = parseInt(this.getAppointmentBranchesLimit, 10);
        
        if (this.getAppointmentBranchesLimit === '-1') {
          // No limit, select all
          this.selectedBranchIds = [
            ...new Set([...this.selectedBranchIds, ...filteredIds])
          ];
        } else {
          // With limit, select up to the limit
          const currentlySelected = this.selectedBranchIds.filter(
            id => !filteredIds.includes(id)
          );
          const availableSlots = limit - currentlySelected.length;
          const toAdd = filteredIds.slice(0, availableSlots);
          this.selectedBranchIds = [...currentlySelected, ...toAdd];
        }
      }
    },
  },
  async mounted() {
    try {
      const branches = await this.$store.dispatch('branch/fetchAllBranches');
      this.branchList = branches;

      this.selectedBranchIds = branches
        .filter(branch => branch.is_active)
        .map(branch => branch.branch_id);
    } catch (err) {
      console.error('Failed to load branches:', err);
      useAlert('Failed to fetch branch list.');
    }
  },
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/60 dark:bg-black/70 backdrop-blur-sm"
  >
    <div
      class="w-full max-w-sm p-6 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 transition-colors"
    >
      <h1 class="mb-4 text-slate-900 dark:text-slate-100 font-semibold">
        Select Active Branches
      </h1>

      <div class="flex justify-between items-start">
        <p :class="['text-sm', selectionStatusClass]">
          {{ selectedBranchIds.length }} /
          {{  getAppointmentBranchesLimit === '-1' ? '∞' :  getAppointmentBranchesLimit }}
          selected
        </p>

        <!-- Select All Checkbox -->
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            :checked="isAllSelected"
            :indeterminate.prop="isIndeterminate"
            @change="toggleSelectAll"
            class="h-5 w-5 rounded-md border-2 border-slate-300 dark:border-slate-500 bg-white dark:bg-slate-800 text-blue-600 focus:ring-2 focus:ring-blue-500 transition cursor-pointer"
          />
          <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
            Select All
          </span>
        </label>
      </div>

      <!-- Search Bar -->
      <div class="mb-4">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search branches..."
          class="w-full p-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition"
        />
      </div>

      <!-- Branch List -->
      <div
        class="grid grid-flow-rows auto-rows-auto gap-2 my-4 max-h-[400px] overflow-auto p-2 rounded-lg bg-slate-100 dark:bg-slate-800 transition-colors"
      >
        <label
          v-for="branch in filteredBranches"
          :key="branch.branch_id"
          class="flex justify-between items-center p-3 rounded-md border border-transparent transition"
          :class="{
            'bg-white hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 cursor-pointer':
              !isBranchDisabled(branch.branch_id),
            'bg-slate-200 dark:bg-slate-700/50 opacity-50 cursor-not-allowed':
              isBranchDisabled(branch.branch_id),
          }"
        >
          <span class="text-slate-900 dark:text-slate-100">
            {{ branch.branch_name }}
          </span>

          <input
            type="checkbox"
            v-model="selectedBranchIds"
            :value="branch.branch_id"
            :disabled="isBranchDisabled(branch.branch_id)"
            class="h-5 w-5 rounded-md border-2 border-slate-300 dark:border-slate-500 bg-white dark:bg-slate-800 text-blue-600 focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 transition"
          />
        </label>

        <!-- Empty State -->
        <div
          v-if="filteredBranches.length === 0"
          class="text-center p-4 text-sm text-slate-500 dark:text-slate-400"
        >
          <span v-if="searchQuery">
            No branches found matching "{{ searchQuery }}"
          </span>
          <span v-else>
            No branches available
          </span>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end pt-2 border-t border-slate-200 dark:border-slate-700">
        <woot-button
          variant="smooth"
          :disabled="isSubmitDisabled"
          @click="updateActiveBranches"
        >
          Confirm Selection
        </woot-button>
      </div>
    </div>
  </div>
</template>